import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Bus, MapPin, Broadcast } from '@phosphor-icons/react';

const osm = (lat, lng) => `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01}%2C${lat - 0.006}%2C${lng + 0.01}%2C${lat + 0.006}&layer=mapnik&marker=${lat}%2C${lng}`;

export default function Transport() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = ['global_admin', 'school_admin', 'principal'].includes(user?.role);
  const isGuardian = user?.role === 'guardian';
  const { data: routes } = useQuery({ queryKey: ['t-routes'], queryFn: () => api.get('/transport/routes').then((r) => r.data) });
  const { data: vehicles, refetch: refetchVehicles } = useQuery({ queryKey: ['t-vehicles'], queryFn: () => api.get('/transport/vehicles').then((r) => r.data), refetchInterval: 15000, enabled: !isGuardian });
  const { data: drivers } = useQuery({ queryKey: ['t-drivers'], queryFn: () => api.get('/transport/drivers').then((r) => r.data), enabled: !isGuardian });
  const { data: optins } = useQuery({ queryKey: ['t-optins'], queryFn: () => api.get('/transport/opt-ins').then((r) => r.data) });
  const { data: plugin } = useQuery({ queryKey: ['t-plugin'], queryFn: () => api.get('/transport/plugin').then((r) => r.data), enabled: isAdmin });
  const { data: alertSettings } = useQuery({ queryKey: ['t-alert-settings'], queryFn: () => api.get('/transport/alert-settings').then((r) => r.data), enabled: isAdmin });
  const { data: students } = useQuery({ queryKey: ['students'], queryFn: () => api.get('/students').then((r) => r.data), enabled: !isGuardian });
  const [rform, setRform] = useState({ name: '', stops: '' });
  const [vform, setVform] = useState({ reg_no: '', gps_device_id: '' });
  const [dform, setDform] = useState({ name: '', phone: '', license_no: '' });
  const [live, setLive] = useState(null);
  const [alertMin, setAlertMin] = useState('');
  useEffect(() => { if (alertSettings) setAlertMin(String(alertSettings.bus_alert_minutes)); }, [alertSettings]);
  const inval = (k) => qc.invalidateQueries({ queryKey: [k] });
  const routeMut = useMutation({
    mutationFn: () => api.post('/transport/routes', { tenant_id: user?.tenant_id, name: rform.name, stops: rform.stops.split('\n').map((l) => { const [name, time, lat, lng] = l.split(','); return { name: (name || '').trim(), time: (time || '').trim(), lat: parseFloat(lat) || 0, lng: parseFloat(lng) || 0 }; }).filter((s) => s.name) }),
    onSuccess: () => { toast.success('Route created'); setRform({ name: '', stops: '' }); inval('t-routes'); }, onError: (e) => toast.error(errMsg(e)),
  });
  const vehicleMut = useMutation({
    mutationFn: () => api.post('/transport/vehicles', { tenant_id: user?.tenant_id, ...vform }),
    onSuccess: () => { toast.success('Vehicle added'); setVform({ reg_no: '', gps_device_id: '' }); inval('t-vehicles'); }, onError: (e) => toast.error(errMsg(e)),
  });
  const driverMut = useMutation({
    mutationFn: () => api.post('/transport/drivers', { tenant_id: user?.tenant_id, ...dform }),
    onSuccess: () => { toast.success('Driver added'); setDform({ name: '', phone: '', license_no: '' }); inval('t-drivers'); }, onError: (e) => toast.error(errMsg(e)),
  });
  const simMut = useMutation({
    mutationFn: (vid) => api.post(`/transport/vehicles/${vid}/simulate`),
    onSuccess: (r) => { toast.success(`Position updated${r.data.stop ? ` - at ${r.data.stop}` : ''}`); refetchVehicles(); }, onError: (e) => toast.error(errMsg(e)),
  });
  const optMut = useMutation({
    mutationFn: (body) => api.post('/transport/opt-ins', body),
    onSuccess: () => { toast.success('Student opted in'); inval('t-optins'); }, onError: (e) => toast.error(errMsg(e)),
  });
  const msgMut = useMutation({
    mutationFn: ({ id, title, body }) => api.post(`/transport/routes/${id}/message`, { title, body }),
    onSuccess: (r) => toast.success(`${r.data.guardians_notified} guardian(s) notified`), onError: (e) => toast.error(errMsg(e)),
  });
  const pluginMut = useMutation({
    mutationFn: (provider) => api.post('/transport/plugin', { provider }),
    onSuccess: (r) => { toast.success(`GPS webhook ready: ${r.data.webhook_path}`); inval('t-plugin'); }, onError: (e) => toast.error(errMsg(e)),
  });
  const alertMut = useMutation({
    mutationFn: () => api.post('/transport/alert-settings', { bus_alert_minutes: Number(alertMin) }),
    onSuccess: (r) => { toast.success(r.data.bus_alert_minutes === 0 ? 'Guardian proximity alerts turned off' : `Guardians alerted when bus is ${r.data.bus_alert_minutes} min away`); inval('t-alert-settings'); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const doMessage = (r) => {
    const title = window.prompt('Message title (e.g. Bus delayed 15 min):'); if (!title) return;
    const body = window.prompt('Details:') || title;
    msgMut.mutate({ id: r.id, title, body });
  };
  const doOptIn = (r) => {
    const sid = window.prompt('Student ID to opt in (copy from Students page):'); if (!sid) return;
    const stop = window.prompt('Pickup stop (optional):') || '';
    optMut.mutate({ student_id: sid.trim(), route_id: r.id, stop });
  };

  return (
    <div data-testid="transport-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">Transport</h1>
      <p className="text-sm text-slate-500 mb-6">Routes, live GPS, opted students, drivers & guardian alerts</p>

      {!isGuardian && (
        <div className="grid lg:grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="vehicles-panel">
            <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Vehicles - live board</p>
            {(vehicles || []).map((v) => (
              <div key={v.id} className="py-2 border-t border-slate-100 text-sm" data-testid={`vehicle-row-${v.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Bus size={16} className={v.online ? 'text-green-600' : 'text-slate-300'} />
                  <span className="font-medium">{v.reg_no}</span>
                  <span className={`font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${v.online ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}>{v.online ? 'live' : 'offline'}</span>
                  {v.position && <span className="text-xs text-slate-400">{v.position.lat.toFixed(4)}, {v.position.lng.toFixed(4)} · {v.position.speed} km/h{v.position.stop_name ? ` · ${v.position.stop_name}` : ''}</span>}
                  {isAdmin && <button data-testid={`simulate-${v.id}`} onClick={() => simMut.mutate(v.id)} className="ms-auto text-xs underline text-slate-400 hover:text-slate-900">simulate ping</button>}
                  {v.position && <button data-testid={`map-${v.id}`} onClick={() => setLive(live === v.id ? null : v.id)} className="text-xs text-orange-700 underline">{live === v.id ? 'hide map' : 'map'}</button>}
                </div>
                {live === v.id && v.position && (
                  <iframe title="map" src={osm(v.position.lat, v.position.lng)} className="w-full h-48 mt-2 rounded border border-slate-200" data-testid="live-map" />
                )}
              </div>
            ))}
            {isAdmin && (
              <div className="flex gap-2 mt-3">
                <Input data-testid="vehicle-reg-input" placeholder="Reg no" className="h-8 text-xs" value={vform.reg_no} onChange={(e) => setVform({ ...vform, reg_no: e.target.value })} />
                <Input data-testid="vehicle-gps-input" placeholder="GPS device id" className="h-8 text-xs" value={vform.gps_device_id} onChange={(e) => setVform({ ...vform, gps_device_id: e.target.value })} />
                <Button data-testid="vehicle-add" size="sm" variant="outline" disabled={!vform.reg_no} onClick={() => vehicleMut.mutate()}>Add</Button>
              </div>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="drivers-panel">
            <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Drivers & BGV</p>
            {(drivers || []).map((d) => (
              <div key={d.id} className="py-2 border-t border-slate-100 text-sm flex items-center gap-2 flex-wrap" data-testid={`driver-row-${d.id}`}>
                <span className="font-medium">{d.name}</span>
                <span className="text-xs text-slate-400">{d.phone} · {d.license_no}</span>
                <span className={`ms-auto font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${d.bgv_status === 'approved' ? 'bg-green-100 text-green-800' : d.bgv_status === 'rejected' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-800'}`} data-testid={`driver-bgv-${d.id}`}>BGV: {d.bgv_status.replace(/_/g, ' ')}</span>
              </div>
            ))}
            {isAdmin && (
              <div className="space-y-2 mt-3">
                <div className="flex gap-2">
                  <Input data-testid="driver-name-input" placeholder="Driver name" className="h-8 text-xs" value={dform.name} onChange={(e) => setDform({ ...dform, name: e.target.value })} />
                  <Input data-testid="driver-phone-input" placeholder="Phone" className="h-8 text-xs" value={dform.phone} onChange={(e) => setDform({ ...dform, phone: e.target.value })} />
                </div>
                <div className="flex gap-2">
                  <Input data-testid="driver-license-input" placeholder="License no" className="h-8 text-xs" value={dform.license_no} onChange={(e) => setDform({ ...dform, license_no: e.target.value })} />
                  <Button data-testid="driver-add" size="sm" variant="outline" disabled={!dform.name} onClick={() => driverMut.mutate()}>Add</Button>
                </div>
              </div>
            )}
          </div>
          {isAdmin && (
            <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="gps-plugin-panel">
              <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">GPS provider plugin</p>
              <p className="text-xs text-slate-500 mb-2">Point your GPS vendor (Traccar, GPSGate, Wialon or any generic tracker) at the webhook - devices push {'{device_id, lat, lng, speed}'} and buses go live.</p>
              {plugin?.has_token
                ? <p className="text-xs font-mono bg-slate-50 border border-slate-200 rounded p-2 break-all" data-testid="gps-webhook-path">POST {plugin.webhook_path}</p>
                : <p className="text-xs text-slate-400">No webhook yet.</p>}
              <div className="flex gap-2 mt-2">
                {(plugin?.providers || []).map((p) => (
                  <button key={p} data-testid={`gps-provider-${p}`} onClick={() => pluginMut.mutate(p)}
                    className={`text-xs border rounded px-2 py-1 ${plugin?.provider === p ? 'border-orange-500 text-orange-700' : 'border-slate-200'}`}>{p}</button>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100" data-testid="bus-alert-settings">
                <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Guardian proximity alert</p>
                <p className="text-xs text-slate-500 mb-2">Guardians are pinged (in-app + email) once a day when the bus is within this many minutes of their child's stop. Set 0 to turn off.</p>
                <div className="flex gap-2 items-center">
                  <Input data-testid="bus-alert-minutes-input" type="number" min="0" max="60" className="h-8 text-xs w-24"
                    value={alertMin} onChange={(e) => setAlertMin(e.target.value)} />
                  <span className="text-xs text-slate-400">minutes</span>
                  <Button data-testid="bus-alert-save" size="sm" variant="outline" disabled={alertMin === '' || alertMut.isPending} onClick={() => alertMut.mutate()}>Save</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="routes-panel">
        <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Routes & opted students</p>
        {(routes || []).map((r) => {
          const opted = (optins || []).filter((o) => o.route_id === r.id);
          return (
            <div key={r.id} className="py-3 border-t border-slate-100" data-testid={`route-row-${r.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <MapPin size={16} className="text-orange-600" />
                <span className="font-semibold">{r.name}</span>
                <span className="text-xs text-slate-400">{(r.stops || []).map((s) => s.name).join(' - ')}</span>
                <span className="ms-auto flex gap-2">
                  {!isGuardian && <span className="font-mono-label text-[9px] uppercase bg-slate-100 rounded px-1.5 py-0.5" data-testid={`route-opted-${r.id}`}>{opted.length} opted</span>}
                  {!isGuardian && user?.role !== 'teacher' && <button data-testid={`route-optin-${r.id}`} onClick={() => doOptIn(r)} className="text-xs underline text-slate-500">+ opt in student</button>}
                  {isAdmin && <button data-testid={`route-message-${r.id}`} onClick={() => doMessage(r)} className="text-xs text-orange-700 flex items-center gap-1"><Broadcast size={13} /> Message guardians</button>}
                </span>
              </div>
              {opted.length > 0 && !isGuardian && (
                <p className="text-xs text-slate-500 mt-1">{opted.map((o) => `${o.student_name} (${o.stop || 'any stop'})`).join(' · ')}</p>
              )}
              {isGuardian && opted.length > 0 && <p className="text-xs text-green-700 mt-1" data-testid={`guardian-optin-${r.id}`}>Your child rides this route: {opted.map((o) => o.student_name).join(', ')}</p>}
            </div>
          );
        })}
        {(routes || []).length === 0 && <p className="text-sm text-slate-400 py-3">No routes yet.</p>}
        {isAdmin && (
          <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
            <Input data-testid="route-name-input" placeholder="Route name (e.g. North Loop)" value={rform.name} onChange={(e) => setRform({ ...rform, name: e.target.value })} />
            <textarea data-testid="route-stops-input" placeholder={'Stops, one per line: name, time, lat, lng\nCentral Park, 07:20, 17.412, 78.463'} value={rform.stops} onChange={(e) => setRform({ ...rform, stops: e.target.value })}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-xs h-20" />
            <Button data-testid="route-create" size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={!rform.name} onClick={() => routeMut.mutate()}>Create route</Button>
          </div>
        )}
      </div>
    </div>
  );
}
